import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function LedOgMuskelsmerterPage() {
  return (
    <>
      <Helmet>
        <title>Led- og Muskelsmerter | Akupunktur Risskov</title>
        <meta
          name="description"
          content="Målrettet akupunktur mod led- og muskelsmerter i Risskov. Blid behandling af gigt, overbelastning og stive led uden ubehag. Læs mere og book tid."
        />
        <meta
          property="og:title"
          content="Led- og Muskelsmerter | Akupunktur Risskov"
        />
        <meta
          property="og:description"
          content="Målrettet akupunktur mod led- og muskelsmerter i Risskov. Blid behandling af gigt, overbelastning og stive led uden ubehag. Læs mere og book tid."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/led-og-muskelsmerter" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Akupunktur mod smerter i muskler og led i Risskov",
            "url": "https://kita-akupunktur.dk/led-og-muskelsmerter",
            "description":
              "Individuelt tilpasset akupunktur ved smerter i led og muskler i Risskov nær Aarhus.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Kita Akupunktur",
              "telephone": "+4529387186",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        {/* Botanical Cherry Branch Accent */}
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={2} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          {/* Breadcrumb navigation */}
          <nav aria-label="Brødkrummesti" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-[#7a5b4f]">
              <li>
                <Link to="/" className="hover:text-[#d74814] transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li>
                <Link to="/akupunktur" className="hover:text-[#d74814] transition-colors">
                  Akupunktur
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li className="text-[#d74814] font-medium" aria-current="page">
                Led og muskelsmerter
              </li>
            </ol>
          </nav>

          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>INDIVIDUELT TILPASSET BEHANDLING • RISSKOV NÆR AARHUS</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427] max-w-4xl leading-tight">
            Akupunktur mod smerter i muskler og led i Risskov
          </h1>

          <p className="text-base sm:text-lg text-[#7a5b4f] mt-4 max-w-3xl leading-relaxed">
            Når smerter i muskler eller led fylder, kan selv små opgaver føles krævende. Hos{" "}
            <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
              Kita Akupunktur
            </Link>{" "}
            i Risskov nær Aarhus tilbyder jeg individuelt tilpasset akupunktur ved smerter i led og muskler samt spændinger. Vi tager udgangspunkt i dine gener, din hverdag og det, du gerne vil kunne igen.
          </p>

          {/* Quick Info Badges */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
            >
              <i className="fa-regular fa-calendar-check text-xs" aria-hidden="true" />
              <span>Bestil tid til behandling</span>
            </Link>

            <a
              href="tel:+4529387186"
              className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white/90 text-[#593427] text-sm font-medium hover:bg-white border border-[#ffe0cf] transition-colors shadow-2xs"
            >
              <i className="fa-solid fa-phone text-[#d74814] text-xs" aria-hidden="true" />
              <span>Tlf. 29 38 71 86</span>
            </a>

            <div className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-[#e1ef9f]/30 border border-[#e1ef9f] text-xs text-[#593427]">
              <i className="fa-solid fa-square-parking text-[#593427]" aria-hidden="true" />
              <span>Gratis parkering ved huset</span>
            </div>

            <div className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-white/80 border border-[#ffe0cf] text-xs text-[#593427]">
              <i className="fa-solid fa-award text-[#d74814]" aria-hidden="true" />
              <span>RAB-registreret akupunktør</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 space-y-12">
        {/* Section 1: Hvor mærker du smerter og spændinger i hverdagen? */}
        <section className="bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-6">
          <div className="flex items-center space-x-3 border-b border-[#ffe0cf]/70 pb-4">
            <div className="w-9 h-9 rounded-full bg-[#ffe0cf]/70 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
              <SakuraFlower className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Hvor mærker du smerter og spændinger i hverdagen?
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              <p>
                Til den første samtale spørger jeg ind til, hvor smerterne sidder, hvor længe de har været der, og hvad der gør dem bedre eller værre. Vi taler også om søvn, arbejde og bevægelse. Fortæl gerne, hvis du allerede har fået en diagnose, tager medicin eller er i et andet behandlingsforløb.
              </p>
              <p>
                Muskelsmerter kan føles som ømhed, spænding eller træthed. Giver dine led smerter ved bestemte bevægelser, er det vigtigt at få afklaret årsagen. Symptomerne fortæller ikke altid, hvad der ligger bag. Har du især gener fra nakken eller ryggen, kan du læse mere om klinikkens tilgang på disse sider:
              </p>
            </div>

            {/* Quick Links Box to Nakke/Skulder & Ryg/Lænd */}
            <div className="lg:col-span-5 bg-[#fffaf7] p-6 rounded-2xl border border-[#ffe0cf] space-y-4 shadow-2xs">
              <div className="flex items-center space-x-2 text-xs font-semibold tracking-wider uppercase text-[#d74814]">
                <i className="fa-solid fa-compass" aria-hidden="true" />
                <span>Læs også mere om klinikkens tilgang</span>
              </div>
              <p className="text-xs text-[#7a5b4f]">
                Oplever du primært spændinger i nakkeområdet eller ondt langs ryggen?
              </p>

              <div className="space-y-3 pt-1">
                <Link
                  to="/spaendinger"
                  className="p-4 rounded-xl bg-white border border-[#ffe0cf] hover:border-[#d74814] hover:shadow-xs transition-all flex items-center justify-between group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-[#ffe0cf]/50 text-[#d74814] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-person-dots-from-line text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="font-serif font-medium text-[#593427] block text-sm sm:text-base group-hover:text-[#d74814] transition-colors">
                        Akupunktur ved spændinger i nakke og skulder
                      </span>
                      <span className="text-xs text-[#7a5b4f]">
                        Lindring af myoser, infiltrationer og spændingshovedpine
                      </span>
                    </div>
                  </div>
                  <i
                    className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform ml-2 shrink-0"
                    aria-hidden="true"
                  />
                </Link>

                <Link
                  to="/ondt-i-ryggen"
                  className="p-4 rounded-xl bg-white border border-[#ffe0cf] hover:border-[#d74814] hover:shadow-xs transition-all flex items-center justify-between group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-[#ffe0cf]/50 text-[#d74814] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-bone text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="font-serif font-medium text-[#593427] block text-sm sm:text-base group-hover:text-[#d74814] transition-colors">
                        Akupunktur ved ondt i ryggen og lænden
                      </span>
                      <span className="text-xs text-[#7a5b4f]">
                        Hjælp ved lumbago, hold i ryggen og iskiassmerter
                      </span>
                    </div>
                  </div>
                  <i
                    className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform ml-2 shrink-0"
                    aria-hidden="true"
                  />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Japansk akupunktur med plads til din sensitivitet */}
        <section className="glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-6">
          <div className="absolute -bottom-10 -right-10 w-48 opacity-15 pointer-events-none">
            <CherryBranchImg index={3} className="w-full h-auto" />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#ffe0cf]/70 pb-4 relative z-10">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <JapaneseBadge text="経絡治療" />
                <span>KEIRAKU CHIRYO MERIDIANTERAPI</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Japansk akupunktur med plads til din sensitivitet
              </h2>
            </div>
            <Link
              to="/japansk-akupunktur"
              className="inline-flex items-center space-x-2 text-xs font-medium text-[#d74814] hover:text-[#b83b0e] underline decoration-[#ffe0cf] self-start sm:self-auto"
            >
              <span>Læs mere om japansk akupunktur</span>
              <i className="fa-solid fa-arrow-right text-2xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative z-10">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              <p>
                Hos Kita Akupunktur arbejder jeg blandt andet med japansk akupunktur i form af Keiraku Chiryo. Det er en traditionel form for meridianterapi, hvor jeg bruger meget tynde nåle. Jeg tilpasser behandlingen efter din sensitivitet og fortæller undervejs, hvad der skal ske, så du kan føle dig godt informeret.
              </p>
              <p>
                Som en del af den japanske metode mærker jeg på din puls og din mave. Det indgår i den traditionelle vurdering, som bruges til at vælge punkter til behandlingen. Vurderingen er ikke det samme som en lægelig undersøgelse og kan ikke afgøre, om dine smerter skyldes en bestemt sygdom eller skade.
              </p>
            </div>

            <div className="lg:col-span-5 bg-white/95 p-6 rounded-2xl border border-[#ffe0cf] space-y-4 shadow-2xs">
              <h3 className="font-serif font-semibold text-[#593427] text-base flex items-center space-x-2">
                <i className="fa-solid fa-leaf text-[#d74814]" aria-hidden="true" />
                <span>Kendetegn ved den japanske metode:</span>
              </h3>
              <ul className="space-y-3 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2.5">
                  <span className="w-5 h-5 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center text-xs shrink-0 mt-0.5">
                    ✓
                  </span>
                  <span>Meget tynde nåle med fokus på behagelig og nænsom stimulering</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-5 h-5 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center text-xs shrink-0 mt-0.5">
                    ✓
                  </span>
                  <span>Traditionel puls- og mavediagnose som vejviser til kroppens meridianer</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-5 h-5 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center text-xs shrink-0 mt-0.5">
                    ✓
                  </span>
                  <span>Løbende forklaring af hvert trin, så du altid føler dig tryg og hørt</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 3: Hvordan føles akupunkturnåle? */}
        <section className="bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-6">
          <div className="flex items-center space-x-3 border-b border-[#ffe0cf]/70 pb-4">
            <div className="w-9 h-9 rounded-full bg-[#ffe0cf]/70 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
              <i className="fa-solid fa-feather-pointed text-base" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Hvordan føles akupunkturnåle?
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {/* Card 1: Nålene */}
            <div className="p-6 rounded-2xl bg-[#fffaf7] border border-[#ffe0cf] space-y-3 flex flex-col justify-between shadow-2xs">
              <div className="space-y-3">
                <div className="inline-flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-[#d74814]">
                  <i className="fa-solid fa-circle-check text-xs" aria-hidden="true" />
                  <span>Tynde og sterile engangsnåle</span>
                </div>
                <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
                  Jeg bruger tynde, sterile engangsnåle. Ved japansk akupunktur kan nålene være helt ned til 0,12 mm tykke. Et stik kan stadig mærkes, og der kan opstå let ømhed eller små blå mærker. Du må altid sige til, hvis noget føles ubehageligt, eller hvis du har brug for en pause under behandlingen.
                </p>
              </div>
              <div className="pt-2 border-t border-[#ffe0cf]/60 text-xs text-[#7a5b4f] italic">
                Behandlingen sker altid i dit rolige tempo.
              </div>
            </div>

            {/* Card 2: Utryghed & Kontaktnåle */}
            <div className="p-6 rounded-2xl bg-[#fffaf7] border border-[#ffe0cf] space-y-3 flex flex-col justify-between shadow-2xs">
              <div className="space-y-3">
                <div className="inline-flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-[#d74814]">
                  <i className="fa-solid fa-hand-holding-heart text-xs" aria-hidden="true" />
                  <span>Utryg ved nåle? Mulighed for kontaktnåle</span>
                </div>
                <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
                  Er du utryg ved nåle, taler vi om det, inden vi begynder. Der er mulighed for at bruge kontaktnåle, som berører huden uden at trænge ind. Valget af teknik sker i dialog med dig. Du behøver derfor ikke på forhånd vide, hvilken type akupunktur du ønsker, eller hvor meget du er tryg ved.
                </p>
              </div>
              <div className="pt-2 border-t border-[#ffe0cf]/60 text-xs text-[#7a5b4f] italic">
                Teknikken tilpasses præcist efter, hvad du har det godt med.
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Et behandlingsforløb ved muskelsmerter og ledsmerter */}
        <section className="glass-card p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-6">
          <div className="flex items-center space-x-3 border-b border-[#ffe0cf]/70 pb-4">
            <div className="w-9 h-9 rounded-full bg-[#ffe0cf]/70 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
              <SakuraFlower className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Et behandlingsforløb ved muskelsmerter og ledsmerter
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              <p>
                Et forløb bliver tilpasset dine gener og dine reaktioner. Ved opfølgningen taler vi om, hvad du har mærket siden sidst. Det kan være ændringer i smerternes styrke, din søvn eller bestemte bevægelser. Dine erfaringer hjælper os med at vurdere, om behandlingen skal justeres eller fortsættes.
              </p>
              <p>
                Der er ikke et fast antal behandlinger, som passer til alle med ømme led og muskelsmerter. Vi aftaler den videre plan undervejs frem for at love et bestemt resultat på forhånd. Målet er også at gøre det tydeligt for dig, hvad vi følger op på, og hvornår vi skal tage stilling til næste skridt.
              </p>
            </div>

            <div className="lg:col-span-5 space-y-3">
              <div className="p-4 rounded-xl bg-white/90 border border-[#ffe0cf] flex items-start space-x-3">
                <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  1
                </span>
                <div>
                  <h4 className="font-serif font-semibold text-[#593427] text-sm sm:text-base">
                    Løbende vurdering
                  </h4>
                  <p className="text-xs text-[#7a5b4f] mt-0.5">
                    Hver session indledes med opfølgning på ændringer i smertebillede, søvn og mobilitet.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-white/90 border border-[#ffe0cf] flex items-start space-x-3">
                <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  2
                </span>
                <div>
                  <h4 className="font-serif font-semibold text-[#593427] text-sm sm:text-base">
                    Ingen tomme løfter
                  </h4>
                  <p className="text-xs text-[#7a5b4f] mt-0.5">
                    Vi aftaler den videre plan i åben dialog, så du altid ved, hvad formålet er med næste skridt.
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-white/90 border border-[#ffe0cf] flex items-start space-x-3">
                <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  3
                </span>
                <div>
                  <h4 className="font-serif font-semibold text-[#593427] text-sm sm:text-base">
                    Gennemskuelighed
                  </h4>
                  <p className="text-xs text-[#7a5b4f] mt-0.5">
                    Tydelighed omkring hvornår vi skal evaluere effekten og tage stilling til forløbet.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Kan akupunktur mod smerter stå alene? */}
        <section className="bg-[#fffaf7] p-8 sm:p-10 rounded-3xl border-2 border-[#ffe0cf] shadow-sm space-y-6 relative overflow-hidden">
          <div className="flex items-center space-x-3 border-b border-[#ffe0cf] pb-4">
            <div className="w-9 h-9 rounded-full bg-[#d74814]/10 border border-[#d74814]/20 flex items-center justify-center text-[#d74814] shrink-0">
              <i className="fa-solid fa-notes-medical text-base" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Kan akupunktur mod smerter stå alene?
              </h2>
            </div>
          </div>

          <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
            <p>
              Akupunktur bør ikke erstatte en nødvendig lægelig vurdering eller anden relevant behandling. Dokumentationen varierer med typen af smerter, og der er ingen garanti for lindring. Jeg tilbyder behandlingen som et muligt supplement, mens træning, medicin eller andre tiltag kan være relevante efter faglig vurdering.
            </p>

            {/* Warning Callout Box for Acute Symptoms */}
            <div className="bg-[#ffe0cf]/40 border-l-4 border-[#d74814] p-5 rounded-r-2xl space-y-2 text-sm sm:text-base text-[#593427]">
              <div className="flex items-center space-x-2 font-semibold text-[#d74814]">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                <span>Vigtigt om akutte symptomer og lægehjælp</span>
              </div>
              <p className="leading-relaxed">
                Nye, uforklarede eller tiltagende smerter, herunder akutte muskelsmerter, bør vurderes af en læge. Søg hurtig lægehjælp ved et rødt, varmt og hævet led, især hvis du også har feber. Det samme gælder ved ny kraftnedsættelse eller problemer med at styre vandladning og afføring. Her skal du ikke vente på en tid til akupunktur.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: Tal med mig om akupunktur i Risskov nær Aarhus */}
        <section className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-md space-y-8 relative overflow-hidden">
          <div className="flex items-center space-x-3 border-b border-[#ffe0cf]/70 pb-4">
            <div className="w-9 h-9 rounded-full bg-[#ffe0cf]/70 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
              <SakuraFlower className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Tal med mig om akupunktur i Risskov nær Aarhus
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
              <p>
                Jeg hedder Mayumi Kita Thiele og er registreret alternativ behandler, også kaldet RAB. Mit arbejde tager udgangspunkt i en rolig samtale og en behandling, der tilpasses dig. Du er velkommen til at spørge ind til min tilgang, så du kan tage stilling til, om et forløb hos mig passer til dine ønsker.
              </p>
              <p>
                Klinikken ligger på Kantorparken 11, 8240 Risskov, med gratis parkering ved huset. Du kan ringe på{" "}
                <a
                  href="tel:+4529387186"
                  className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b83b0e]"
                >
                  29 38 71 86
                </a>{" "}
                eller kontakte mig om en tid. Fortæl gerne kort om dine smerter og eventuelle spørgsmål, så vi sammen kan afklare, hvad næste skridt skal være.
              </p>

              {/* Practical Info List */}
              <div className="pt-2 space-y-2.5">
                <div className="flex items-center space-x-3 text-sm text-[#593427]">
                  <i className="fa-solid fa-location-dot text-[#d74814] w-5 text-center" aria-hidden="true" />
                  <span>
                    <strong>Adresse:</strong> Kantorparken 11, 8240 Risskov
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-[#593427]">
                  <i className="fa-solid fa-square-parking text-[#d74814] w-5 text-center" aria-hidden="true" />
                  <span>
                    <strong>Parkering:</strong> Gratis parkering direkte ved huset
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-[#593427]">
                  <i className="fa-solid fa-phone text-[#d74814] w-5 text-center" aria-hidden="true" />
                  <span>
                    <strong>Telefon:</strong> 29 38 71 86
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-[#593427]">
                  <i className="fa-solid fa-award text-[#d74814] w-5 text-center" aria-hidden="true" />
                  <span>
                    <strong>Kvalitet:</strong> RAB-registreret akupunktør (mulighed for tilskud fra fx Sygeforsikringen "danmark")
                  </span>
                </div>
              </div>
            </div>

            {/* Practical Contact Card */}
            <div className="lg:col-span-5 bg-[#fffaf7] p-6 rounded-2xl border border-[#ffe0cf] space-y-4 shadow-2xs">
              <h3 className="font-serif font-semibold text-[#593427] text-lg">
                Har du spørgsmål eller vil du bestille tid?
              </h3>
              <p className="text-xs sm:text-sm text-[#7a5b4f]">
                Ring direkte, eller send en uforpligtende besked via formularen nedenfor. Jeg vender hurtigt tilbage.
              </p>

              <div className="space-y-3 pt-2">
                <a
                  href="tel:+4529387186"
                  className="w-full inline-flex items-center justify-center space-x-2 px-5 py-3 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring på 29 38 71 86</span>
                </a>

                <Link
                  to="/priser"
                  className="w-full inline-flex items-center justify-center space-x-2 px-5 py-2.5 rounded-xl bg-white text-[#593427] text-sm font-medium hover:bg-white/80 border border-[#ffe0cf] transition-colors"
                >
                  <i className="fa-solid fa-tags text-[#d74814]" aria-hidden="true" />
                  <span>Se priser og tilskudsmuligheder</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Embedded Contact Form */}
          <div className="pt-6 border-t border-[#ffe0cf]/80">
            <div className="mb-4">
              <h3 className="text-xl font-serif text-[#593427]">
                Send en besked til Mayumi
              </h3>
              <p className="text-xs sm:text-sm text-[#7a5b4f] mt-1">
                Beskriv kort dine smerter eller spørgsmål, så kontakter jeg dig snarest.
              </p>
            </div>
            <ContactForm />
          </div>
        </section>

        {/* Section: Relaterede behandlingsområder */}
        <section className="p-6 sm:p-8 rounded-3xl bg-[#fff5ee] border border-[#ffe0cf] space-y-4">
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
              Relaterede behandlingsområder
            </div>
            <p className="text-base font-serif text-[#593427]">
              Læs også om akupunktur til andre specifikke smertetilstande og spændinger
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Link
              to="/ondt-i-ryggen"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Ondt i ryggen & lænden →
            </Link>
            <Link
              to="/spaendinger"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Spændinger i nakke & skuldre →
            </Link>
            <Link
              to="/ondt-i-kaeben"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Ondt i kæben & tyggemuskler →
            </Link>
            <Link
              to="/migraene"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Hovedpine & migræne →
            </Link>
            <Link
              to="/japansk-akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Japansk akupunktur (blid) →
            </Link>
            <Link
              to="/oere-akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Øre akupunktur →
            </Link>
          </div>
          {/* Hurtige genveje */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-4 text-xs sm:text-sm">
            <Link
              to="/priser"
              className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
            >
              Se priser & tilskud fra “danmark”
            </Link>
            <Link
              to="/om-mig"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Læs om Mayumi Kita Thiele
            </Link>
            <Link
              to="/faq"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Samlet FAQ
            </Link>
            <Link
              to="/kontakt"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Bestil tid i Risskov
            </Link>
          </div>
        </section>

        {/* FAQ Section sidst på siden */}
        <section className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
          <FaqSection
            title="Ofte stillede spørgsmål om led- og muskelsmerter"
            subtitle="Få svar på hvordan akupunktur lindrer gigtsmerter, overbelastningsskader, tennisalbue, frossen skulder og stivhed i kroppens led."
            faqs={[
              {
                question: "Hvilke typer led- og muskelsmerter kan behandles med akupunktur?",
                answer: (
                  <>
                    Akupunktur er velegnet til en bred vifte af smerter i bevægeapparatet – herunder slidgigt (artrose) i knæ, hofte og fingre, frossen skulder, tennis- og golfalbue, hælspore, achillessenebetændelse samt{" "}
                    <Link to="/ondt-i-ryggen" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      lænde- og rygsmerter
                    </Link>{" "}
                    og{" "}
                    <Link to="/spaendinger" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      nakkespændinger
                    </Link>
                    . Behandlingen dæmper inflammation omkring sener og slimsække, mindsker hævelse og forbedrer vævets naturlige helingsevne.
                  </>
                ),
              },
              {
                question: "Hvordan virker akupunktur smertelindrende på betændte og stive led?",
                answer:
                  "Nålene stimulerer de perifere nervefibre, hvilket sender beroligende signaler til centralnervesystemet om at udskille kroppens egne smertelindrende stoffer såsom endorfiner. Samtidig øges den lokale mikrocirkulation dramatisk omkring leddet, så ophobede affalds- og betændelsesstoffer skylles bort, mens iltrigt blod fremmer vævets smidighed og nedsætter morgenstivhed.",
              },
              {
                question: "Kan akupunktur reducere mit behov for smertestillende medicin?",
                answer: (
                  <>
                    Mange klienter oplever, at et målrettet akupunkturforløb reducerer deres smerteintensitet så markant, at de i samråd med egen læge kan trappe ned på forbruget af smertestillende medicin. Akupunktur belaster hverken maveslimhinde, nyrer eller kredsløb, og som RAB-behandler er der mulighed for tilskud fra Sygeforsikring “danmark”. Læs mere under{" "}
                    <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      priser og tilskud
                    </Link>
                    .
                  </>
                ),
              },
              {
                question: "Hvor mange behandlinger skal der til ved kroniske smerter som f.eks. slidgigt?",
                answer:
                  "Ved kroniske ledsmerter som slidgigt er formålet at fjerne den konstante smerte, reducere leddets stivhed og forbedre din bevægelsesfrihed i hverdagen. De fleste mærker en klar lindring efter 3-4 sessioner, og et typisk grundforløb består af 5-6 behandlinger. Herefter vælger mange en opfølgende vedligeholdelsesbehandling hver 4.-6. uge for at bevare det gode resultat.",
              },
              {
                question: "Gør behandlingen ondt, hvis leddet eller musklen er meget betændt og berøringsfølsomt?",
                answer: (
                  <>
                    Nej, behandlingen tilpasses altid nænsomt efter dit smerteniveau. Hvis et led er meget ømt, stikker jeg sjældent direkte i selve smertestedet. I stedet benytter jeg{" "}
                    <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      japansk meridianterapi
                    </Link>{" "}
                    eller{" "}
                    <Link to="/oere-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      øreakupunktur
                    </Link>{" "}
                    med distale reflekspunkter på arme, ben eller øret. Dette sender stærke smertedæmpende impulser til det plagede led helt uden ubehag eller lokal irritation.
                  </>
                ),
              },
            ]}
          />
        </section>
      </div>
    </>
  );
}
